/* ================================================================
   ListDefinitivePage.tsx — Liste définitive des élèves
   V7 — Parité avec StudentsPage : même endpoint /students, mêmes filtres
   (active_only) → effectifs identiques page Élèves / liste définitive.
   ================================================================ */

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Users, Printer, FileSpreadsheet, Download,
  Loader2, Search, Settings, Calendar,
  Building2, Save, ChevronDown, ChevronUp,
  CheckSquare, Square
} from "lucide-react";
import { apiService } from "@/services/api";
import { toast } from "sonner";
import { useLang } from "@/hooks/useLang";

// ==================== HELPERS ====================
const extractData = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (res && typeof res === "object") {
    // V5 : version robuste (identique à la liste provisoire) — cherche aussi
    // dans les objets imbriqués : data.items, data.students, data.eleves...
    // Sans cela, /classes/{id}/students mal lu -> fallback sur /students global
    // paginé (20 max) -> "certains noms n'apparaissent pas".
    const keys = ["data", "items", "results", "students", "eleves", "rows"];
    for (const k of keys) {
      if (Array.isArray(res[k])) return res[k];
      if (res[k] && typeof res[k] === "object" && !Array.isArray(res[k])) {
        for (const k2 of keys) {
          if (Array.isArray(res[k][k2])) return res[k][k2];
        }
      }
    }
  }
  return [];
};

const AUTH_STORAGE_KEY = "auth-storage";
const SELECTED_YEAR_ID_KEY = "lybibal-selected-year-id";
const SELECTED_YEAR_LABEL_KEY = "lybibal-selected-year-label";
const SELECTED_YEAR_EVENT = "lybibal-year-change";

const getUserSchoolId = (): number | null => {
  try {
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (authRaw) {
      const parsed = JSON.parse(authRaw);
      const state = parsed?.state || parsed;
      if (state?.user?.school_id) return Number(state.user.school_id);
    }
  } catch (e) { console.warn("Failed to get school_id:", e); }
  return null;
};

const getSelectedYearId = (): number | null => {
  try {
    const yearId = localStorage.getItem(SELECTED_YEAR_ID_KEY);
    if (yearId) return Number(yearId);
  } catch (e) { console.warn("Failed to get year_id:", e); }
  return null;
};

function getValue(obj: any, ...keys: string[]): any {
  if (!obj || typeof obj !== "object") return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
    for (const subKey of Object.keys(obj)) {
      const sub = obj[subKey];
      if (sub && typeof sub === "object" && !Array.isArray(sub)) {
        if (sub[key] !== undefined && sub[key] !== null) return sub[key];
      }
    }
  }
  return undefined;
}

function getStr(obj: any, ...keys: string[]): string {
  const v = getValue(obj, ...keys);
  return v !== undefined && v !== null ? String(v) : "";
}

function extractBool(obj: any, ...keys: string[]): boolean {
  if (!obj || typeof obj !== "object") return false;
  const truthy = [true, "true", "True", "TRUE", 1, "1", "yes", "Yes", "YES", "oui", "Oui", "OUI"];
  const falsy = [false, "false", "False", "FALSE", 0, "0", "no", "No", "NO", "non", "Non", "NON"];
  for (const key of keys) {
    const v = obj[key];
    if (truthy.includes(v)) return true;
    if (falsy.includes(v)) return false;
    for (const subKey of Object.keys(obj)) {
      const sub = obj[subKey];
      if (sub && typeof sub === "object" && !Array.isArray(sub)) {
        const sv = sub[key];
        if (truthy.includes(sv)) return true;
        if (falsy.includes(sv)) return false;
      }
    }
  }
  return false;
}

/** Cherche is_repeating dans l'objet et ses sous-objets courants (student, enrollments, etc.) */
function findRepeating(obj: any): boolean {
  if (!obj || typeof obj !== "object") return false;
  const candidates = [
    obj,
    obj.student,
    obj.enrollments?.[0],
    obj.enrollment,
    obj.student_enrollments?.[0],
    obj.school_enrollments?.[0],
  ];
  for (const cand of candidates) {
    if (cand && typeof cand === "object") {
      const v = cand.is_repeating;
      if (v === true || v === "true" || v === 1 || v === "1" || v === "yes" || v === "oui") return true;
      if (v === false || v === "false" || v === 0 || v === "0" || v === "no" || v === "non") return false;
    }
  }
  return false;
}

function calculateAge(birthDate: string): number {
  if (!birthDate) return 0;
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age > 0 ? age : 0;
}

function downloadCSV(filename: string, rows: string[][], headerRows?: string[][]) {
  const allRows = headerRows ? [...headerRows, ...rows] : rows;
  const csv = allRows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

function downloadExcelData(filename: string, data: any[]) {
  if (data.length === 0) return;
  const headers = Object.keys(data[0]);
  const rows = [headers, ...data.map(row => headers.map(h => row[h] ?? ""))];
  downloadCSV(filename, rows);
}

function headerEnDelegation(fr: string): string {
  const map: Record<string, string> = {
    "DÉLÉGATION DÉPARTEMENTALE DES ENSEIGNEMENTS SECONDAIRES": "DIVISIONAL DELEGATION FOR SECONDARY EDUCATION",
  };
  return map[fr] || fr;
}
// ==================== ORDRE OFFICIEL DES CLASSES (exports) ====================
function stripAccents(s: string): string {
  return (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/**
 * V4 — Clé de tri officielle des classes pour les exports :
 * Sixième (1-6) → Cinquième (1-6) → Quatrième (ALL1-2, ESP1-2)
 * → Troisième (ALL1-2, ESP1-3) → Seconde (ALL, ESP, C1-3)
 * → Première (ALL, ESP, PD1-3, PC) → Terminale (ALL, ESP, D1-3, C)
 * → section anglophone : Form 1-5 → Lower Sixth → Upper Sixth.
 * Classe non reconnue : à la fin (tri alphabétique en secours).
 */
function classSortKey(name: string): [number, number, number] {
  const n = stripAccents(name).toLowerCase().trim();
  const tail = n.match(/(\d+)\s*$/);
  const tailNum = tail ? parseInt(tail[1], 10) : 0;
  const hasAll = /(^|\s)all\d*($|\s)/.test(n) || /allemand/.test(n);
  const hasEsp = /(^|\s)esp\d*($|\s)/.test(n) || /espagnol/.test(n);
  const hasPC  = /(^|\s)pc($|\s)/.test(n);
  const hasC   = /(^|\s)c\s*-?\s*\d*($|\s)/.test(n) && !hasPC;
  const hasPD  = /(^|\s)p?d\s*-?\s*\d+($|\s)/.test(n);

  let group: number;
  if (/sixieme|(^|\s)6e($|\s)|(^|\s)6eme($|\s)/.test(n)) group = 1;
  else if (/cinquieme|(^|\s)5e($|\s)|(^|\s)5eme($|\s)/.test(n)) group = 2;
  else if (/quatrieme|(^|\s)4e($|\s)|(^|\s)4eme($|\s)/.test(n)) group = 3;
  else if (/troisieme|(^|\s)3e($|\s)|(^|\s)3eme($|\s)/.test(n)) group = 4;
  else if (/seconde|(^|\s)2nde?($|\s)/.test(n)) group = 5;
  else if (/premiere|(^|\s)1ere($|\s)|(^|\s)1re($|\s)/.test(n)) group = 6;
  else if (/terminale|(^|\s)tle($|\s)/.test(n)) group = 7;
  else if (/form\s*(one|1)/.test(n)) return [8, 1, 0];
  else if (/form\s*(two|2)/.test(n)) return [8, 2, 0];
  else if (/form\s*(three|3)/.test(n)) return [8, 3, 0];
  else if (/form\s*(four|4)/.test(n)) return [8, 4, 0];
  else if (/form\s*(five|5)/.test(n)) return [8, 5, 0];
  else if (/lower\s*sixth/.test(n)) return [9, 1, 0];
  else if (/upper\s*sixth/.test(n)) return [10, 1, 0];
  else return [99, 0, 0];

  if (group <= 2) return [group, 0, tailNum];    // Sixième / Cinquième : n° seul

  let sub: number;
  if (group === 3 || group === 4) {              // Quatrième / Troisième : ALL puis ESP
    sub = hasAll ? 1 : hasEsp ? 2 : 0;
  } else if (group === 5) {                      // Seconde : ALL, ESP, C1-3
    sub = hasAll ? 1 : hasEsp ? 2 : hasC ? 3 : 0;
  } else if (group === 6) {                      // Première : ALL, ESP, PD1-3, PC
    sub = hasAll ? 1 : hasEsp ? 2 : hasPD ? 3 : hasPC ? 4 : 0;
  } else {                                       // Terminale : ALL, ESP, D1-3, C
    sub = hasAll ? 1 : hasEsp ? 2 : hasPD ? 3 : hasC ? 4 : 0;
  }
  return [group, sub, tailNum];
}



// ==================== TYPES ====================
interface SchoolHeader {
  republic_fr: string;
  republic_en: string;
  motto_fr: string;
  motto_en: string;
  region: string;
  department: string;
  delegation: string;
  sub_division: string;
  school_name: string;
  school_name_en: string;
  address: string;
  po_box: string;
  logo_url: string;
}

interface Student {
  id: number;
  class_id?: number;
  class_name?: string;
  matricule: string;
  first_name: string;
  last_name: string;
  gender: string;
  birth_date: string;
  birth_place: string;
  is_repeating: boolean;
  status?: string;   // V3 : statut (ANCIEN/INACTIVE exclus des listes)
}

/**
 * V7 — NE force plus la classe demandée : la classe affichée est la classe
 * d'inscription réelle renvoyée par l'API (champ class_id, objet class/classe,
 * ou enrollment.class_id). Les paramètres classId/className ne servent plus
 * qu'en dernier recours si l'API ne dit rien.
 */
function normalizeStudent(s: any, classId?: number, className?: string): Student {
  const stu = s.student || s;
  const resolvedClassId = classId ?? (() => {
    const v = getValue(s, "class_id", "student_class_id", "classe_id", "classId");
    if (v !== undefined && v !== null) return Number(v);
    const clsObj = s.class || s.classe || stu.class;
    if (clsObj && typeof clsObj === "object" && clsObj.id !== undefined && clsObj.id !== null) return Number(clsObj.id);
    const enr = s.enrollment || s.enrollments?.[0] || stu.enrollment;
    if (enr && typeof enr === "object" && enr.class_id !== undefined && enr.class_id !== null) return Number(enr.class_id);
    return undefined;
  })();
  const clsObj = s.class || s.classe || stu.class;
  const nameFromStr = getStr(s, "class_name", "classe_name", "className");
  const nameFromObj = clsObj && typeof clsObj === "object" ? getStr(clsObj, "name", "label") : "";
  const resolvedClassName = className ?? (nameFromStr || nameFromObj || undefined);
  return {
    id: getValue(stu, "id", "student_id") ?? 0,
    class_id: resolvedClassId,
    class_name: resolvedClassName,
    matricule: getStr(stu, "matricule", "registration_number", "matricule_nat", "code", "student_matricule"),
    first_name: getStr(stu, "first_name", "prenom", "firstname", "firstName", "prenoms", "given_name", "name"),
    last_name: getStr(stu, "last_name", "nom", "lastname", "lastName", "noms", "family_name", "surname"),
    gender: getStr(stu, "gender", "sex", "sexe", "genre"),
    birth_date: getStr(stu, "birth_date", "date_of_birth", "date_naissance", "dob", "birthdate"),
    birth_place: getStr(stu, "birth_place", "place_of_birth", "lieu_naissance", "birthplace"),
    is_repeating: findRepeating(s),
    status: getStr(stu, "status"),
  };
}


export default function ListDefinitivePage() {
  const { lang } = useLang();

  const [schoolId, setSchoolId] = useState<number | null>(getUserSchoolId);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<number | null>(getSelectedYearId);
  const [currentAcademicYear, setCurrentAcademicYear] = useState<{id: number; name: string} | null>(null);

  const t = {
    fr: {
      title: "Liste définitive des élèves",
      subtitle: "Liste définitive par classe — Année académique",
      listTitle: "LISTES DEFINITIVES DES ELEVES",
      configTitle: "Configuration",
      section: "Section", cycle: "Cycle", level: "Niveau", class: "Classe",
      allSections: "Toutes les sections", allCycles: "Tous les cycles",
      allLevels: "Tous les niveaux", allClasses: "Toutes les classes",
      academicYear: "Année académique",
      search: "Rechercher...",
      no: "N°", matricule: "Matricule Nat.", name: "Nom(s) et prénom(s)",
      sex: "Sx", birthDate: "Né(e) le", age: "Age", birthPlace: "Lieu de naissance",
      repeating: "Red", yes: "Oui", noText: "Non",
      exportPDF: "PDF", exportExcel: "Excel", exportExcelData: "Excel Data",
      noData: "Aucune donnée", loading: "Chargement...",
      noYearSelected: "Aucune année scolaire sélectionnée",
      yearRequired: "Veuillez sélectionner une année scolaire dans le header",
      classLabel: "Classe",
      totalStudents: "Total élèves",
      headerTitle: "En-tête de l'établissement",
      headerDesc: "Personnalisez l'en-tête qui apparaît sur chaque liste",
      schoolName: "Nom de l'établissement",
      schoolNameEn: "Nom (EN)",
      region: "Région",
      department: "Département",
      delegation: "Délégation",
      subDivision: "Arrondissement / Sub-Division",
      address: "Adresse",
      poBox: "BP / P.O. Box",
      logo: "Logo",
      saveHeader: "Sauvegarder l'en-tête",
    },
    en: {
      title: "Definitive Student List",
      subtitle: "Definitive list by class — Academic year",
      listTitle: "DEFINITIVE STUDENT LIST",
      configTitle: "Configuration",
      section: "Section", cycle: "Cycle", level: "Level", class: "Class",
      allSections: "All sections", allCycles: "All cycles",
      allLevels: "All levels", allClasses: "All classes",
      academicYear: "Academic year",
      search: "Search...",
      no: "No.", matricule: "National Reg.", name: "Name(s) and first name(s)",
      sex: "Sex", birthDate: "Born on", age: "Age", birthPlace: "Birth place",
      repeating: "Rep.", yes: "Yes", noText: "No",
      exportPDF: "PDF", exportExcel: "Excel", exportExcelData: "Excel Data",
      noData: "No data", loading: "Loading...",
      noYearSelected: "No academic year selected",
      yearRequired: "Please select an academic year in the header",
      classLabel: "Class",
      totalStudents: "Total students",
      headerTitle: "School Header",
      headerDesc: "Customize the header that appears on each list",
      schoolName: "School Name",
      schoolNameEn: "Name (EN)",
      region: "Region",
      department: "Department",
      delegation: "Delegation",
      subDivision: "Sub Division",
      address: "Address",
      poBox: "P.O. Box",
      logo: "Logo",
      saveHeader: "Save Header",
    }
  }[lang];

  const [sections, setSections] = useState<any[]>([]);
  const [cycles, setCycles] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [years, setYears] = useState<any[]>([]);

  const [selectedSection, setSelectedSection] = useState<string>("");
  const [selectedCycle, setSelectedCycle] = useState<string>("");
  const [selectedLevel, setSelectedLevel] = useState<string>("");
  const [selectedClassIds, setSelectedClassIds] = useState<Set<number>>(new Set());

  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [diagInfo, setDiagInfo] = useState<string>("");

  const [showHeaderConfig, setShowHeaderConfig] = useState(false);
  const [schoolHeader, setSchoolHeader] = useState<SchoolHeader>({
    republic_fr: "RÉPUBLIQUE DU CAMEROUN",
    republic_en: "REPUBLIC OF CAMEROON",
    motto_fr: "Paix-Travail-Patrie",
    motto_en: "Peace-Work-Fatherland",
    region: "RÉGION DE L'OUEST",
    department: "DÉPARTEMENT DE LA MIFI",
    delegation: "DÉLÉGATION DÉPARTEMENTALE DES ENSEIGNEMENTS SECONDAIRES",
    sub_division: "ARRONDISSEMENT DE BAFOUSSAM II",
    school_name: "LYCÉE BILINGUE DE BALENG",
    school_name_en: "GBHS BALENG",
    address: "BP : 1041 Baleng",
    po_box: "P.O. Box : 1041 Baleng",
    logo_url: "",
  });

  const toggleClass = (id: number) => {
    const next = new Set(selectedClassIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedClassIds(next);
  };
  const selectAllClasses = () => setSelectedClassIds(new Set(filteredClasses.map((c: any) => Number(c.id))));
  const deselectAllClasses = () => setSelectedClassIds(new Set());

  const getFilterParams = useCallback(() => {
    const params: Record<string, any> = {};
    if (schoolId) params.school_id = schoolId;
    if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId;
    return params;
  }, [schoolId, selectedAcademicYearId]);

  // ==================== FILTRES PROGRESSIFS ====================
  const filteredCycles = useMemo(() => {
    if (!selectedSection) return cycles;
    return cycles.filter((c: any) => String(c.section_id) === selectedSection);
  }, [cycles, selectedSection]);

  const filteredLevels = useMemo(() => {
    if (!selectedCycle) {
      if (!selectedSection) return levels;
      const cycleIds = cycles
        .filter((c: any) => String(c.section_id) === selectedSection)
        .map((c: any) => String(c.id));
      return levels.filter((l: any) => cycleIds.includes(String(l.cycle_id)));
    }
    return levels.filter((l: any) => String(l.cycle_id) === selectedCycle);
  }, [levels, cycles, selectedCycle, selectedSection]);

  const filteredClasses = useMemo(() => {
    if (selectedLevel) {
      return classes.filter((c: any) => String(c.level_id) === selectedLevel || String(c.levelId) === selectedLevel);
    }
    if (selectedCycle) {
      const levelIds = filteredLevels.map((l: any) => String(l.id));
      return classes.filter((c: any) => levelIds.includes(String(c.level_id)) || levelIds.includes(String(c.levelId)));
    }
    if (selectedSection) {
      const cycleIds = filteredCycles.map((c: any) => String(c.id));
      const levelIds = levels
        .filter((l: any) => cycleIds.includes(String(l.cycle_id)))
        .map((l: any) => String(l.id));
      return classes.filter((c: any) => levelIds.includes(String(c.level_id)) || levelIds.includes(String(c.levelId)));
    }
    return classes;
  }, [classes, selectedLevel, selectedCycle, selectedSection, filteredLevels, levels, filteredCycles]);

  // Synchroniser selectedClassIds avec les classes visibles
  useEffect(() => {
    const visibleIds = new Set(filteredClasses.map((c: any) => Number(c.id)));
    setSelectedClassIds(prev => {
      const next = new Set<number>();
      prev.forEach(id => { if (visibleIds.has(id)) next.add(id); });
      if (next.size === 0 && filteredClasses.length > 0) {
        filteredClasses.forEach((c: any) => next.add(Number(c.id)));
      }
      return next;
    });
  }, [filteredClasses]);

  // ==================== CHARGEMENT DES DONNÉES ====================
  useEffect(() => {
    const loadAllData = async () => {
      if (!schoolId || !selectedAcademicYearId) return;
      setLoading(true);
      try {
        const params = getFilterParams();
        const [yearRes, secRes, cycRes, levRes, clsRes] = await Promise.all([
          apiService.get(`/schools/${schoolId}/academic-years`).catch(() => ({ data: [] })),
          apiService.get("/sections", { params }).catch(() => ({ data: [] })),
          apiService.get("/cycles", { params }).catch(() => ({ data: [] })),
          apiService.get("/levels", { params }).catch(() => ({ data: [] })),
          apiService.get("/classes", { params }).catch(() => ({ data: [] })),
        ]);

        setYears(extractData(yearRes));
        setSections(extractData(secRes));
        setCycles(extractData(cycRes));
        setLevels(extractData(levRes));
        const loadedClasses = extractData(clsRes);
        setClasses(loadedClasses);

        // ══════════ V7 — MÊME SOURCE QUE StudentsPage (parité garantie) ══════════
        // Les effectifs de la liste définitive DOIVENT être IDENTIQUES à ceux
        // de la page Gestion des élèves : même endpoint /students, mêmes
        // paramètres (school_id + academic_year_id + active_only=true),
        // avec pagination pour tout récupérer. L'ancien endpoint par classe
        // /classes/{id}/students est abandonné : c'était lui qui désynchronisait
        // les comptes après les transferts.
        const OUT_STATUSES = ['DEMISSION', 'EXCLU', 'RENVOYE', 'TRANSFERE', 'DIPLOME', 'ANCIEN', 'INACTIVE'];
        let all: Student[] = [];
        const seenIds = new Set<number>();
        let apiTotal = 0;
        let pageN = 1;
        while (pageN <= 60) { // 60 pages × 200 = 12 000 élèves max
          const res: any = await apiService.get("/students", {
            params: { ...params, active_only: true, page: pageN, per_page: 200, _ts: Date.now() },
          });
          const arr = extractData(res);
          apiTotal = (res && typeof res.total === "number") ? res.total
            : (res?.data && typeof res.data.total === "number") ? res.data.total
            : arr.length;
          for (const s of arr) {
            const stu = normalizeStudent(s);
            if (!stu.id || seenIds.has(stu.id)) continue;
            // class_id absent de la réponse → résolution par nom de classe
            if ((stu.class_id === undefined || stu.class_id === null) && stu.class_name) {
              const clsObj = loadedClasses.find((c: any) => c.name === stu.class_name);
              if (clsObj) stu.class_id = Number(clsObj.id);
            }
            seenIds.add(stu.id);
            all.push(stu);
          }
          if (arr.length === 0 || all.length >= apiTotal) break;
          pageN++;
        }

        // Sécurité : exclusion des statuts sortants même si le backend
        // n'avait pas appliqué active_only (le filtre serveur suffit en principe).
        const beforeFilter = all.length;
        all = all.filter(s => !s.status || !OUT_STATUSES.includes(String(s.status).toUpperCase()));
        if (beforeFilter - all.length > 0) console.log(`[Listes] ${beforeFilter - all.length} élève(s) sortant(s) exclu(s) en sécurité`);

        // DIAGNOSTIC visible + console
        const withoutClass = all.filter(s => s.class_id === undefined).length;
        console.log(`[Listes] ${loadedClasses.length} classes | ${all.length} élève(s) actif(s) via /students (total API: ${apiTotal}) | ${withoutClass} sans class_id`);
        setDiagInfo(`${loadedClasses.length} classe(s) • ${all.length} élève(s) actif(s) — source identique à la page Élèves • ${withoutClass} sans classe`);
        setAllStudents(all.sort((a, b) => a.last_name.localeCompare(b.last_name)));
      } catch (e) {
        console.error("Load error:", e);
        setAllStudents([]);
      } finally {
        setLoading(false);
      }
    };
    loadAllData();
  }, [schoolId, selectedAcademicYearId, getFilterParams]);

  // ==================== ÉLÈVES AFFICHÉS ====================
  const displayStudents = useMemo(() => {
    let result = [...allStudents];
    if (selectedClassIds.size > 0) {
      result = result.filter(s => s.class_id !== undefined && selectedClassIds.has(Number(s.class_id)));
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(s =>
        `${s.last_name} ${s.first_name}`.toLowerCase().includes(term) ||
        s.matricule.toLowerCase().includes(term)
      );
    }
    return result;
  }, [allStudents, selectedClassIds, searchTerm]);

  const yearLabel = years.find((y: any) => y.id === selectedAcademicYearId)?.label || years.find((y: any) => y.id === selectedAcademicYearId)?.name || "2025-2026";

  // Helper commun : regroupe les élèves affichés par classe (ordre identique
  // au PDF — utilisé par les exports CSV, Excel Data, XLSX ET l'impression).
  // Helper commun : regroupe les élèves affichés par classe — utilisé par les
  // exports CSV, Excel Data, XLSX ET l'impression. V4 : classes triées dans
  // l'ordre officiel (Sixième → Terminale → section anglophone).
  const groupDisplayedByClass = (): [number, Student[]][] => {
    const groupedByClass: Record<number, Student[]> = {};
    displayStudents.forEach(s => {
      const cid = s.class_id || 0;
      if (!groupedByClass[cid]) groupedByClass[cid] = [];
      groupedByClass[cid].push(s);
    });
    const getName = (cid: number): string =>
      classes.find((c: any) => c.id === cid)?.name
      || groupedByClass[cid]?.[0]?.class_name
      || "";
    return (Object.entries(groupedByClass) as [string, Student[]][])
      .map(([cid, list]) => [Number(cid), list] as [number, Student[]])
      .sort((a, b) => {
        const ka = classSortKey(getName(a[0]));
        const kb = classSortKey(getName(b[0]));
        for (let i = 0; i < 3; i++) { if (ka[i] !== kb[i]) return ka[i] - kb[i]; }
        return getName(a[0]).localeCompare(getName(b[0]), "fr");
      });
  };

  // ==================== EXPORT CSV ====================
  const handleExportCSV = () => {
    const selectedClassNames = filteredClasses
      .filter((c: any) => selectedClassIds.has(c.id))
      .map((c: any) => c.name)
      .join(", ");
    const headerRows = [
      [schoolHeader.school_name],
      [t.listTitle],
      [`${t.academicYear}: ${yearLabel} | ${t.classLabel}: ${selectedClassNames || (lang === "fr" ? "Toutes" : "All")}`],
      [],
    ];
    const rows: string[][] = [];
    // V2 : même structure que le PDF — une section par classe, numérotation
    // qui repart à 1 dans chaque classe, séparateur entre les classes.
    groupDisplayedByClass().forEach(([cid, clsStudents], clsIdx) => {
      const cls = classes.find((c: any) => c.id === cid);
      const clsName = cls?.name || clsStudents[0]?.class_name || (lang === "fr" ? "Toutes" : "All");
      if (clsIdx > 0) rows.push([]); // ligne vide entre deux classes
      rows.push([`${t.classLabel} : ${clsName}`]);
      rows.push([`${t.academicYear} : ${yearLabel} | ${t.totalStudents} : ${clsStudents.length}`]);
      rows.push([]);
      rows.push([t.no, t.matricule, t.name, t.sex, t.birthDate, t.age, t.birthPlace, t.repeating]);
      clsStudents.forEach((s, i) => {
        rows.push([
          String(i + 1), s.matricule, `${s.last_name} ${s.first_name}`,
          s.gender, s.birth_date, String(calculateAge(s.birth_date)),
          s.birth_place, s.is_repeating ? t.yes : t.noText
        ]);
      });
    });
    // Pied de fichier
    rows.push([]);
    rows.push(["myhelperschool.cm"]);
    downloadCSV(`liste-definitive-${selectedClassNames || "all"}.csv`, rows, headerRows);
    toast.success("Export CSV généré");
  };

  // ==================== EXPORT XLSX STYLÉ (ExcelJS) ====================
  // Un fichier .xlsx VRAI (pas un CSV déguisé) : une FEUILLE PAR CLASSE
  // (comme une page par classe dans le PDF), en-tête officiel BILINGUE en
  // couleur, tableau à bandeaux alternés avec bordures, pied de page
  // myhelperschool.cm, prêt pour l'impression A4.
  const handleExportXLSX = async () => {
    try {
      const ExcelJS: any = await import("exceljs");
      const wb = new ExcelJS.Workbook();
      const BLUE = "FF1E3A5F";   // bleu en-tête (identique au PDF)
      const ALT  = "FFF9FAFB";   // bandeau alterné
      const GRAY = "FFDDDDDD";   // bordures
      const entries = groupDisplayedByClass();
      if (entries.length === 0) { toast.error(t.noData); return; }

      const border = {
        top: { style: "thin", color: { argb: GRAY } },
        left: { style: "thin", color: { argb: GRAY } },
        bottom: { style: "thin", color: { argb: GRAY } },
        right: { style: "thin", color: { argb: GRAY } },
      };

      entries.forEach(([cid, clsStudents]) => {
        const cls = classes.find((c: any) => c.id === cid);
        const clsName = cls?.name || clsStudents[0]?.class_name || (lang === "fr" ? "Toutes" : "All");
        const ws = wb.addWorksheet(clsName.substring(0, 31), {
          pageSetup: { paperSize: 9, orientation: "portrait", fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
        });
        // Pied de page : myhelperschool.cm centré, 9pt, gris — sur chaque page
        ws.headerFooter = {
          oddFooter: "&C&9&K666666myhelperschool.cm",
          evenFooter: "&C&9&K666666myhelperschool.cm",
        };
        ws.columns = [
          { width: 6 }, { width: 15 }, { width: 34 }, { width: 7 },
          { width: 13 }, { width: 6 }, { width: 22 }, { width: 7 },
        ];

        // ══ Bloc en-tête officiel BILINGUE — identique au PDF ══
        // 3 zones : FR à gauche (cols 1-3) | logo (cols 4-5) | EN à droite (cols 6-8)
        const regionEn = schoolHeader.region.replace("RÉGION DE L'", "WEST ").replace("RÉGION", "REGION");
        const deptEn = schoolHeader.department.replace("DÉPARTEMENT DE LA ", "").replace("DÉPARTEMENT", "DIVISION");
        const delegEn = headerEnDelegation(schoolHeader.delegation);
        const subdEn = schoolHeader.sub_division.replace("ARRONDISSEMENT", "SUB DIVISION");
        const leftLines = [
          schoolHeader.republic_fr.toUpperCase(),
          schoolHeader.motto_fr,
          schoolHeader.region,
          schoolHeader.department,
          schoolHeader.delegation,
          schoolHeader.sub_division,
        ];
        const rightLines = [
          schoolHeader.republic_en.toUpperCase(),
          schoolHeader.motto_en,
          regionEn, deptEn, delegEn, subdEn,
        ];
        for (let i = 0; i < 6; i++) {
          const row = ws.addRow([]);
          row.height = i === 0 ? 18 : 15;
          ws.mergeCells(i + 1, 1, i + 1, 3); // zone FR
          ws.mergeCells(i + 1, 4, i + 1, 5); // zone logo
          ws.mergeCells(i + 1, 6, i + 1, 8); // zone EN
          const lc = row.getCell(1);
          lc.value = leftLines[i];
          lc.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
          lc.font = i === 0 ? { bold: true, size: 11, color: { argb: BLUE } } : { size: 9, color: { argb: "FF555555" } };
          const rc = row.getCell(6);
          rc.value = rightLines[i];
          rc.alignment = { horizontal: "right", vertical: "middle", wrapText: true };
          rc.font = i === 0 ? { bold: true, size: 11, color: { argb: BLUE } } : { size: 9, color: { argb: "FF555555" } };
          // Zone logo : pastille bleue sur les 6 lignes
          for (const col of [4, 5]) {
            const zc = row.getCell(col);
            zc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BLUE } };
          }
        }
        // Logo : image si un logo a été téléversé (data URL), sinon "LB"
        const logoCell = ws.getCell(1, 4);
        if (schoolHeader.logo_url && schoolHeader.logo_url.startsWith("data:")) {
          try {
            const m = schoolHeader.logo_url.match(/^data:image\/(png|jpe?g|gif);base64,(.+)$/);
            if (m) {
              const imageId = wb.addImage({ base64: m[2], extension: m[1] === "jpeg" ? "jpeg" : m[1] });
              ws.addImage(imageId, { tl: { col: 3.5, row: 0.7 }, ext: { width: 52, height: 52 } });
            }
          } catch (imgErr) { console.warn("[XLSX] logo ignoré:", imgErr); }
        }
        logoCell.value = schoolHeader.logo_url && schoolHeader.logo_url.startsWith("data:") ? "" : "LB";
        logoCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
        logoCell.alignment = { horizontal: "center", vertical: "middle" };

        // ── Nom de l'établissement + titre (centrés) ──
        const mergeAll = (row: any) => ws.mergeCells(row.number, 1, row.number, 8);
        const rName = ws.addRow([schoolHeader.school_name]);
        rName.font = { bold: true, size: 14, color: { argb: BLUE } };
        rName.alignment = { horizontal: "center" }; mergeAll(rName);
        const rNameEn = ws.addRow([schoolHeader.school_name_en]);
        rNameEn.font = { bold: true, size: 11, color: { argb: BLUE } };
        rNameEn.alignment = { horizontal: "center" }; mergeAll(rNameEn);
        const rAddr = ws.addRow([`${schoolHeader.address}  |  ${schoolHeader.po_box}`]);
        rAddr.font = { size: 9, color: { argb: "FF666666" } };
        rAddr.alignment = { horizontal: "center" }; mergeAll(rAddr);
        // Ligne de séparation bleue (comme la bordure du PDF)
        rAddr.eachCell((cell: any) => { cell.border = { bottom: { style: "medium", color: { argb: BLUE } } }; });
        const rTitle = ws.addRow([t.listTitle]);
        rTitle.font = { bold: true, size: 16, color: { argb: BLUE } };
        rTitle.alignment = { horizontal: "center" }; mergeAll(rTitle);
        const rMeta = ws.addRow([`${t.academicYear} : ${yearLabel}  |  ${t.classLabel} : ${clsName}  |  ${t.totalStudents} : ${clsStudents.length}`]);
        rMeta.font = { size: 10, color: { argb: "FF666666" } };
        rMeta.alignment = { horizontal: "center" }; mergeAll(rMeta);
        ws.addRow([]); // ligne vide

        // ── En-tête du tableau (fond bleu, texte blanc) ──
        const hr = ws.addRow([t.no, t.matricule, t.name, t.sex, t.birthDate, t.age, t.birthPlace, t.repeating]);
        hr.eachCell((cell: any) => {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BLUE } };
          cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10 };
          cell.alignment = { horizontal: "center", vertical: "middle" };
          cell.border = border;
        });
        hr.height = 20;

        // ── Lignes élèves (bandeaux alternés + bordures) ──
        clsStudents.forEach((s, i) => {
          const row = ws.addRow([
            i + 1, s.matricule, `${s.last_name} ${s.first_name}`,
            s.gender, s.birth_date, calculateAge(s.birth_date),
            s.birth_place, s.is_repeating ? t.yes : t.noText,
          ]);
          row.eachCell((cell: any, col: number) => {
            cell.border = border;
            cell.font = { size: 10 };
            cell.alignment = { horizontal: col === 3 || col === 7 ? "left" : "center", vertical: "middle" };
            if (i % 2 === 1) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ALT } };
          });
          if (s.is_repeating) {
            const repCell = row.getCell(8);
            repCell.font = { size: 10, bold: true, color: { argb: "FF92400E" } };
            repCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFEF3C7" } };
          }
        });
      });

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const selectedClassNames = filteredClasses
        .filter((c: any) => selectedClassIds.has(c.id))
        .map((c: any) => c.name)
        .join(", ");
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `liste-definitive-${selectedClassNames || "all"}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Export Excel (.xlsx) généré");
    } catch (e) {
      console.error("[XLSX] ExcelJS indisponible:", e);
      toast.error(lang === "fr"
        ? "Bibliothèque exceljs manquante — installez-la : npm install exceljs (export CSV utilisé en secours)"
        : "exceljs library missing — run: npm install exceljs (CSV fallback used)");
      handleExportCSV();
    }
  };

  // ==================== EXPORT EXCEL DATA ====================
  const handleExportExcelData = () => {
    const selectedClassNames = filteredClasses
      .filter((c: any) => selectedClassIds.has(c.id))
      .map((c: any) => c.name)
      .join(", ");
    const emptyRow = { [t.no]: "", [t.matricule]: "", [t.name]: "", [t.sex]: "", [t.birthDate]: "", [t.age]: "", [t.birthPlace]: "", [t.repeating]: "" };
    const headerData = [
      { ...emptyRow, [t.matricule]: schoolHeader.school_name },
      { ...emptyRow, [t.matricule]: t.listTitle },
      { ...emptyRow, [t.matricule]: `${t.academicYear}: ${yearLabel}`, [t.name]: `${t.classLabel}: ${selectedClassNames || (lang === "fr" ? "Toutes" : "All")}` },
      { ...emptyRow },
    ];
    const data: any[] = [...headerData];
    // V2 : même structure que le PDF — une section par classe, numérotation
    // qui repart à 1 dans chaque classe, ligne vide entre les classes.
    groupDisplayedByClass().forEach(([cid, clsStudents], clsIdx) => {
      const cls = classes.find((c: any) => c.id === cid);
      const clsName = cls?.name || clsStudents[0]?.class_name || (lang === "fr" ? "Toutes" : "All");
      if (clsIdx > 0) data.push({ ...emptyRow }); // ligne vide entre deux classes
      data.push({ ...emptyRow, [t.name]: `${t.classLabel} : ${clsName}` });
      data.push({ ...emptyRow, [t.name]: `${t.academicYear} : ${yearLabel} | ${t.totalStudents} : ${clsStudents.length}` });
      data.push({ ...emptyRow });
      clsStudents.forEach((s, i) => {
        data.push({
          [t.no]: i + 1, [t.matricule]: s.matricule, [t.name]: `${s.last_name} ${s.first_name}`,
          [t.sex]: s.gender, [t.birthDate]: s.birth_date, [t.age]: calculateAge(s.birth_date),
          [t.birthPlace]: s.birth_place, [t.repeating]: s.is_repeating ? t.yes : t.noText,
        });
      });
    });
    // Pied de fichier
    data.push({ ...emptyRow });
    data.push({ ...emptyRow, [t.name]: "myhelperschool.cm" });
    downloadExcelData(`liste-definitive-data-${selectedClassNames || "all"}.csv`, data);
    toast.success("Excel Data exporté");
  };

  // ==================== SAUVEGARDE EN-TÊTE ====================
  const handleSaveHeader = () => {
    localStorage.setItem("lybibal-school-header", JSON.stringify(schoolHeader));
    toast.success(lang === "fr" ? "En-tête sauvegardé" : "Header saved");
  };

  // ==================== IMPRESSION PDF ====================
  const handlePrintPDF = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) { toast.error("Autorisez les popups"); return; }
    const today = new Date().toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB");

    const logoHtml = schoolHeader.logo_url
      ? `<img src="${schoolHeader.logo_url}" style="width:55px;height:55px;object-fit:contain;" />`
      : `<div style="width:50px;height:50px;background:#1e3a5f;border-radius:50%;display:flex;align-items:center;justify-content:center;"><span style="color:#fff;font-size:14px;font-weight:bold;">LB</span></div>`;

    // Grouper les élèves affichés par classe (helper commun aux 3 exports)
    const classEntries = groupDisplayedByClass();

    let fullHTML = "";
    classEntries.forEach(([cid, clsStudents], clsIdx) => {
      const cls = classes.find((c: any) => c.id === Number(cid));
      const currentClsName = cls?.name || clsStudents[0]?.class_name || (lang === "fr" ? "Toutes" : "All");
      const rowsHTML = clsStudents.map((s, i) =>
        `<tr style="background:${i % 2 === 0 ? "#fff" : "#f9fafb"};">
          <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${i + 1}</td>
          <td style="border:1px solid #ddd;padding:4px 6px;font-size:10px;font-family:monospace;">${s.matricule}</td>
          <td style="border:1px solid #ddd;padding:4px 6px;font-size:10px;font-weight:600;">${s.last_name} ${s.first_name}</td>
          <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${s.gender}</td>
          <td style="border:1px solid #ddd;padding:4px 6px;font-size:10px;">${s.birth_date}</td>
          <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${calculateAge(s.birth_date)}</td>
          <td style="border:1px solid #ddd;padding:4px 6px;font-size:10px;">${s.birth_place}</td>
          <td style="border:1px solid #ddd;padding:4px 6px;text-align:center;font-size:10px;">${s.is_repeating ? "Oui" : "Non"}</td>
        </tr>`
      ).join("");

      const pageBreak = clsIdx > 0 ? '<div style="page-break-before:always;"></div>' : "";

      fullHTML += `${pageBreak}
      <div style="text-align:center;border-bottom:2px solid #1e3a5f;padding-bottom:10px;margin-bottom:15px;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;">
          <div style="flex:1;text-align:left;">
            <p style="margin:0;font-size:11px;font-weight:bold;color:#1e3a5f;text-transform:uppercase;">${schoolHeader.republic_fr}</p>
            <p style="margin:2px 0;font-size:9px;color:#555;">${schoolHeader.motto_fr}</p>
            <p style="margin:4px 0 1px 0;font-size:9px;color:#333;">${schoolHeader.region}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${schoolHeader.department}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${schoolHeader.delegation}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${schoolHeader.sub_division}</p>
          </div>
          <div style="width:70px;text-align:center;margin:0 12px;flex-shrink:0;">${logoHtml}</div>
          <div style="flex:1;text-align:right;">
            <p style="margin:0;font-size:11px;font-weight:bold;color:#1e3a5f;text-transform:uppercase;">${schoolHeader.republic_en}</p>
            <p style="margin:2px 0;font-size:9px;color:#555;">${schoolHeader.motto_en}</p>
            <p style="margin:4px 0 1px 0;font-size:9px;color:#333;">${schoolHeader.region.replace("RÉGION DE L'", "WEST ").replace("RÉGION", "REGION")}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${schoolHeader.department.replace("DÉPARTEMENT DE LA ", "").replace("DÉPARTEMENT", "DIVISION")}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${headerEnDelegation(schoolHeader.delegation)}</p>
            <p style="margin:1px 0;font-size:9px;color:#333;">${schoolHeader.sub_division.replace("ARRONDISSEMENT", "SUB DIVISION")}</p>
          </div>
        </div>
        <div style="margin-top:8px;">
          <p style="margin:0;font-size:14px;font-weight:bold;color:#1e3a5f;">${schoolHeader.school_name}</p>
          <p style="margin:2px 0;font-size:11px;font-weight:600;color:#1e3a5f;">${schoolHeader.school_name_en}</p>
          <p style="margin:2px 0;font-size:9px;color:#666;">${schoolHeader.address} | ${schoolHeader.po_box}</p>
        </div>
      </div>
      <h1 style="text-align:center;font-size:22px;font-weight:bold;color:#1e3a5f;margin:12px 0;text-transform:uppercase;letter-spacing:1px;">${t.listTitle}</h1>
      <div style="text-align:center;margin:10px 0 14px 0;">
        <span style="font-size:11px;"><strong>${t.academicYear}:</strong> ${yearLabel}</span>
        <span style="font-size:24px;font-weight:bold;color:#1e3a5f;margin:0 24px;">${t.classLabel} : ${currentClsName}</span>
        <span style="font-size:11px;"><strong>${t.totalStudents}:</strong> ${clsStudents.length}</span>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:10px;">
        <thead>
          <tr>
            <th colspan="8" style="padding:2px 6px;font-size:8px;font-weight:normal;color:#999;text-align:left;border:none;background:#fff;">${today} &nbsp;—&nbsp; ${t.title} &nbsp;—&nbsp; ${t.classLabel} : ${currentClsName}</th>
          </tr>
          <tr style="background:#1e3a5f;color:#fff;">
            <th style="padding:6px;border:1px solid #ddd;">${t.no}</th>
            <th style="padding:6px;border:1px solid #ddd;">${t.matricule}</th>
            <th style="padding:6px;border:1px solid #ddd;">${t.name}</th>
            <th style="padding:6px;border:1px solid #ddd;">${t.sex}</th>
            <th style="padding:6px;border:1px solid #ddd;">${t.birthDate}</th>
            <th style="padding:6px;border:1px solid #ddd;">${t.age}</th>
            <th style="padding:6px;border:1px solid #ddd;">${t.birthPlace}</th>
            <th style="padding:6px;border:1px solid #ddd;">${t.repeating}</th>
          </tr>
        </thead>
        <tbody>${rowsHTML}</tbody>
      </table>
      <div style="margin-top:35px;display:flex;justify-content:flex-end;page-break-inside:avoid;">
        <div style="text-align:center;margin-right:40px;">
          <p style="margin:0;font-size:10px;color:#333;">Fait à Baleng, le ${today}</p>
          <p style="margin:35px 0 0;font-size:10px;font-weight:bold;color:#1e3a5f;text-transform:uppercase;">Le Proviseur / The Principal</p>
        </div>
      </div>
      <div style="margin-top:20px;text-align:center;font-size:9px;color:#999;border-top:1px solid #eee;padding-top:8px;">${schoolHeader.school_name} — ${t.title} — ${t.classLabel} : ${currentClsName} — ${today}</div>`;
    });

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${t.title}</title>
      <style>*{-webkit-print-color-adjust:exact;print-color-adjust:exact}@page{size:A4;margin:10mm 10mm 16mm}body{font-family:Arial,sans-serif;margin:0;padding:10px 10px 28px;font-size:11px}table{width:100%;border-collapse:collapse;font-size:10px;border:2px solid #333!important}thead{display:table-header-group}tr{page-break-inside:avoid}th{background:#1e3a5f!important;color:#fff!important;font-weight:bold!important;padding:6px;border:1.5px solid #333!important}td{border:1.5px solid #444!important;padding:4px 6px}
      /* Pied de page répété en bas de CHAQUE page imprimée */
      .print-footer{position:fixed;bottom:0;left:0;right:0;text-align:center;font-size:9px;color:#666;border-top:1px solid #ddd;padding-top:4px;background:#fff}
      @media print{.no-print{display:none}}</style></head><body>${fullHTML}
      <div class="print-footer">myhelperschool.cm — Téléchargé sur https://myhelperschool.cm/</div>
      <div class="no-print" style="text-align:center;margin-top:15px;">
        <button onclick="window.print()" style="padding:8px 24px;background:#1e3a5f;color:#fff;border:none;border-radius:4px;cursor:pointer;">${lang === "fr" ? "Imprimer" : "Print"}</button>
      </div></body></html>`;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  // ==================== INIT ====================
  useEffect(() => {
    const yearId = getSelectedYearId();
    const yearLabel = localStorage.getItem(SELECTED_YEAR_LABEL_KEY);
    if (yearId) {
      setSelectedAcademicYearId(yearId);
      if (yearLabel) setCurrentAcademicYear({ id: yearId, name: yearLabel });
    }
    try {
      const storedSchool = localStorage.getItem("lybibal-selected-school-id");
      if (storedSchool) setSchoolId(Number(storedSchool));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.year_id) {
        setSelectedAcademicYearId(Number(detail.year_id));
        if (detail.label) setCurrentAcademicYear({ id: Number(detail.year_id), name: detail.label });
        setAllStudents([]);
      }
    };
    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange);
    return () => window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange);
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("lybibal-school-header");
      if (saved) setSchoolHeader(JSON.parse(saved));
    } catch { /* ignore */ }
  }, []);

  if (!selectedAcademicYearId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-md">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t.noYearSelected}</h2>
          <p className="text-sm text-gray-500">{t.yearRequired}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Users className="w-7 h-7 text-[#1e3a8a]" />
          {t.title}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
        {currentAcademicYear && (
          <div className="flex items-center gap-2 mt-2">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">{currentAcademicYear.name}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* ===== PANNEAU CONFIGURATION ===== */}
        <div className="xl:col-span-1 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Settings className="w-4 h-4 text-[#1e3a8a]" />
              {t.configTitle}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.academicYear}</label>
                <select
                  value={selectedAcademicYearId ?? ""}
                  onChange={(e) => setSelectedAcademicYearId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                >
                  <option value="">{t.academicYear}</option>
                  {years.map((y: any) => <option key={y.id} value={String(y.id)}>{y.label || y.name}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.section}</label>
                <select
                  value={selectedSection}
                  onChange={(e) => { setSelectedSection(e.target.value); setSelectedCycle(""); setSelectedLevel(""); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                >
                  <option value="">{t.allSections}</option>
                  {sections.map((s: any) => <option key={s.id} value={String(s.id)}>{s.name || s.label_fr}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.cycle}</label>
                <select
                  value={selectedCycle}
                  onChange={(e) => { setSelectedCycle(e.target.value); setSelectedLevel(""); }}
                  disabled={!selectedSection}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">{t.allCycles}</option>
                  {filteredCycles.map((c: any) => <option key={c.id} value={String(c.id)}>{c.name || c.label_fr}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.level}</label>
                <select
                  value={selectedLevel}
                  onChange={(e) => { setSelectedLevel(e.target.value); }}
                  disabled={!selectedCycle}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">{t.allLevels}</option>
                  {filteredLevels.map((l: any) => <option key={l.id} value={String(l.id)}>{l.name || l.label_fr}</option>)}
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium text-gray-600">{t.class}</label>
                  <span className="text-xs text-gray-500">{selectedClassIds.size} {lang === "fr" ? "classe(s) sélectionnée(s)" : "class(es) selected"}</span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <button onClick={selectAllClasses} className="text-xs text-blue-600 hover:text-blue-700 font-medium">{lang === "fr" ? "Tout sélectionner" : "Select all"}</button>
                  <span className="text-gray-300">|</span>
                  <button onClick={deselectAllClasses} className="text-xs text-gray-500 hover:text-gray-700 font-medium">{lang === "fr" ? "Tout désélectionner" : "Deselect all"}</button>
                </div>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {filteredClasses.map((c: any) => (
                    <label key={c.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-all text-xs ${selectedClassIds.has(c.id) ? "bg-blue-50 border border-blue-200" : "bg-white border border-gray-100 hover:bg-gray-50"}`}>
                      {selectedClassIds.has(c.id) ? <CheckSquare className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" /> : <Square className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                      <input type="checkbox" checked={selectedClassIds.has(c.id)} onChange={() => toggleClass(c.id)} className="sr-only" />
                      <span className="font-medium text-gray-700 truncate">{c.name}</span>
                    </label>
                  ))}
                </div>
                {filteredClasses.length === 0 && <p className="text-xs text-orange-600 mt-1">{lang === "fr" ? "Aucune classe trouvée." : "No classes found."}</p>}
              </div>
            </div>
          </div>

          {/* ===== CONFIG EN-TÊTE ===== */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <button onClick={() => setShowHeaderConfig(!showHeaderConfig)} className="flex items-center justify-between w-full text-sm font-semibold text-gray-900">
              <span className="flex items-center gap-2"><Building2 className="w-4 h-4 text-[#1e3a8a]" />{t.headerTitle}</span>
              {showHeaderConfig ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showHeaderConfig && (
              <div className="mt-4 space-y-3">
                <p className="text-xs text-gray-500">{t.headerDesc}</p>
                <div><label className="block text-xs text-gray-600 mb-1">{t.schoolName}</label><input value={schoolHeader.school_name} onChange={(e) => setSchoolHeader(p => ({ ...p, school_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.schoolNameEn}</label><input value={schoolHeader.school_name_en} onChange={(e) => setSchoolHeader(p => ({ ...p, school_name_en: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.region}</label><input value={schoolHeader.region} onChange={(e) => setSchoolHeader(p => ({ ...p, region: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.department}</label><input value={schoolHeader.department} onChange={(e) => setSchoolHeader(p => ({ ...p, department: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.delegation}</label><input value={schoolHeader.delegation} onChange={(e) => setSchoolHeader(p => ({ ...p, delegation: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.subDivision}</label><input value={schoolHeader.sub_division} onChange={(e) => setSchoolHeader(p => ({ ...p, sub_division: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.address}</label><input value={schoolHeader.address} onChange={(e) => setSchoolHeader(p => ({ ...p, address: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.poBox}</label><input value={schoolHeader.po_box} onChange={(e) => setSchoolHeader(p => ({ ...p, po_box: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t.logo}</label>
                  <input type="file" accept="image/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => setSchoolHeader(p => ({ ...p, logo_url: reader.result as string })); reader.readAsDataURL(file); } }} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                  {schoolHeader.logo_url && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={schoolHeader.logo_url} alt="Logo" className="w-12 h-12 object-contain border rounded" />
                      <button onClick={() => setSchoolHeader(p => ({ ...p, logo_url: "" }))} className="text-xs text-red-600 hover:text-red-800 underline">{lang === "fr" ? "Supprimer" : "Remove"}</button>
                    </div>
                  )}
                </div>
                <button onClick={handleSaveHeader} className="w-full px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] flex items-center justify-center gap-2"><Save className="w-4 h-4" />{t.saveHeader}</button>
              </div>
            )}
          </div>
        </div>

        {/* ===== PANNEAU LISTE ===== */}
        <div className="xl:col-span-2 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="text" placeholder={t.search} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] w-full" />
            </div>
            <div className="h-6 w-px bg-gray-200 hidden sm:block" />
            <button onClick={handlePrintPDF} className="px-4 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 flex items-center gap-2 transition-colors"><Printer className="w-4 h-4" />{t.exportPDF}</button>
            <button onClick={handleExportXLSX} className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 flex items-center gap-2 transition-colors"><FileSpreadsheet className="w-4 h-4" />{t.exportExcel} (.xlsx)</button>
            <button onClick={handleExportExcelData} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 flex items-center gap-2 transition-colors"><Download className="w-4 h-4" />{t.exportExcelData}</button>
          </div>

          {diagInfo && (
            <div className="text-xs font-mono text-gray-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              🩺 Diagnostic : {diagInfo} — classes cochées : {selectedClassIds.size}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1e3a5f] text-white">
                  <tr>
                    <th className="px-3 py-2 text-center text-xs font-semibold">{t.no}</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold">{t.matricule}</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold">{t.name}</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold">{t.sex}</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold">{t.birthDate}</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold">{t.age}</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold">{t.birthPlace}</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold">{t.repeating}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /><p className="text-sm text-gray-500 mt-2">{t.loading}</p></td></tr>
                  ) : displayStudents.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-500"><Users className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p className="text-sm">{t.noData}</p></td></tr>
                  ) : (
                    displayStudents.map((s, i) => (
                      <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-2 text-center text-gray-500 text-xs">{i + 1}</td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-600">{s.matricule}</td>
                        <td className="px-3 py-2 font-medium text-gray-900 text-xs">{s.last_name} {s.first_name}</td>
                        <td className="px-3 py-2 text-center text-gray-500 text-xs">{s.gender}</td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{s.birth_date}</td>
                        <td className="px-3 py-2 text-center text-gray-500 text-xs">{calculateAge(s.birth_date)}</td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{s.birth_place}</td>
                        <td className="px-3 py-2 text-center"><span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${s.is_repeating ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"}`}>{s.is_repeating ? t.yes : t.noText}</span></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {displayStudents.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                <span className="text-xs text-gray-500">{displayStudents.length} {lang === "fr" ? "élève(s)" : "student(s)"}</span>
                <span className="text-xs text-gray-500">{t.classLabel}: {filteredClasses.filter((c: any) => selectedClassIds.has(c.id)).map((c: any) => c.name).join(", ") || (lang === "fr" ? "Toutes" : "All")}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}